import { useState } from "react";
import { toast } from "sonner";
import api, { fmtErr } from "@/lib/api";
import { useFetch, PageHeader, Loading, EmptyState, ErrorState, SectionCard, StatusChip, Paginator, fmtDate, ExportButton } from "@/lib/ui";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { useSelection, RowCheck, SelectAllCheck, BulkDeleteBar } from "@/lib/bulk";

const ROLES = ["all", "customer", "shopkeeper", "delivery_partner", "admin"];

export default function Users() {
  const [role, setRole] = useState("all");
  const [search, setSearch] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const limit = 20;
  const path = `/admin/users?page=${page}&limit=${limit}${role !== "all" ? `&role=${role}` : ""}${q ? `&search=${encodeURIComponent(q)}` : ""}`;
  const { data, loading, error, refetch, setData } = useFetch(path, [path]);
  const sel = useSelection(data?.items);

  const toggle = async (u) => {
    const next = !(u.is_active !== false);
    try {
      await api.patch(`/admin/users/${u.id}/status`, { is_active: next });
      setData({ ...data, items: data.items.map((x) => (x.id === u.id ? { ...x, is_active: next } : x)) });
      toast.success(`${u.name} ${next ? "activated" : "deactivated"}`);
    } catch (e) { toast.error(fmtErr(e)); }
  };

  return (
    <div>
      <PageHeader title="Users & Customers" subtitle="Manage all platform accounts and access"
        actions={<ExportButton filename="sonu-users.csv" rows={data?.items} testid="export-users-btn"
          columns={[{ key: "name", label: "Name" }, { key: "phone", label: "Phone" }, { key: "email", label: "Email" }, { key: "role", label: "Role" }, { key: "is_active", label: "Active", get: (r) => (r.is_active !== false ? "Yes" : "No") }, { key: "referral_code", label: "Referral Code" }, { key: "created_at", label: "Joined" }, { key: "id", label: "User ID" }]} />} />
      <div className="flex flex-col sm:flex-row gap-2 mb-4">
        <form onSubmit={(e) => { e.preventDefault(); setPage(1); setQ(search); }} className="flex-1 flex gap-2">
          <Input placeholder="Search name, phone or email…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="users-search-input" />
          <Button type="submit" variant="outline" data-testid="users-search-btn">Search</Button>
        </form>
        <Select value={role} onValueChange={(v) => { setRole(v); setPage(1); }}>
          <SelectTrigger className="w-full sm:w-48" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
          <SelectContent>
            {ROLES.map((r) => <SelectItem key={r} value={r}>{r === "all" ? "All roles" : r.replace(/_/g, " ")}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <BulkDeleteBar entity="users" selection={sel} onDone={refetch} label="users" />
      <SectionCard className="overflow-hidden">
        {loading ? <Loading /> : error ? <ErrorState error={error} onRetry={refetch} /> :
          !data?.items?.length ? <EmptyState label="No users found" /> : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="users-table">
                <thead className="bg-muted/50 text-left text-xs text-muted-foreground">
                  <tr>
                    <th className="px-4 py-2.5 w-10"><SelectAllCheck checked={sel.allChecked} onCheck={sel.toggleAll} /></th>
                    <th className="px-4 py-2.5 font-semibold">Name</th>
                    <th className="px-4 py-2.5 font-semibold">Contact</th>
                    <th className="px-4 py-2.5 font-semibold">Role</th>
                    <th className="px-4 py-2.5 font-semibold">Joined</th>
                    <th className="px-4 py-2.5 font-semibold text-right">Active</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.items.map((u) => (
                    <tr key={u.id} className="hover:bg-muted/30" data-testid={`user-row-${u.id}`}>
                      <td className="px-4 py-2.5"><RowCheck checked={sel.selected.has(u.id)} onCheck={() => sel.toggle(u.id)} testid={`user-select-${u.id}`} /></td>
                      <td className="px-4 py-2.5 font-medium">{u.name}</td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs font-mono">{u.phone}{u.email ? ` · ${u.email}` : ""}</td>
                      <td className="px-4 py-2.5"><StatusChip status={u.role?.toUpperCase()} tone={u.role === "admin" ? "amber" : "blue"} /></td>
                      <td className="px-4 py-2.5 text-muted-foreground text-xs">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-2.5 text-right">
                        {u.role === "admin" ? <span className="text-xs text-muted-foreground">—</span> :
                          <Switch checked={u.is_active !== false} onCheckedChange={() => toggle(u)} data-testid={`user-toggle-${u.id}`} />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </SectionCard>
      <Paginator page={page} total={data?.total} limit={limit} onPage={setPage} />
    </div>
  );
}
